// =============================================================================
// MessageComposer/composerLayout.ts — Lógica pura do layout responsivo do
// compositor (F38-S15).
//
// Bug corrigido: a linha do compositor usava breakpoints `sm:` do TAILWIND,
// que reagem à largura do VIEWPORT, não à largura real do contêiner. Com o
// painel do contato aberto (o estado padrão), a coluna da conversa fica bem
// mais estreita mesmo em telas grandes — o viewport continua "desktop", mas
// o espaço disponível para o compositor já não é. Resultado: os botões de
// ícone não encolhiam, e sobravam ~64px pro campo de texto.
//
// A correção mede a largura REAL do contêiner (via ResizeObserver, ver
// `useElementWidth.ts`) e decide o layout (empilhado vs. em linha) a partir
// dela — igual ao que já acontecia no breakpoint mobile, só que agora
// funciona também com o painel do contato aberto no desktop.
// =============================================================================

/** Alvo de toque mínimo por ícone — doc 18 §"Acessibilidade" (40×40px). */
export const COMPOSER_BUTTON_SIZE_PX = 40;

/** `gap-2` do Tailwind entre os itens da linha do compositor. */
export const COMPOSER_GAP_PX = 8;

/** `px-3` (12px) de cada lado do contêiner da linha. */
export const COMPOSER_ROW_PADDING_PX = 24;

/**
 * Piso do campo de texto — largura mínima para caber pelo menos uma frase
 * curta sem quebrar sílaba por sílaba (~20-25 caracteres em `text-sm`).
 */
export const COMPOSER_FIELD_MIN_WIDTH_PX = 160;

/**
 * Decide se a linha do compositor deve empilhar (botões acima, campo em
 * linha própria) em vez de ficar tudo em uma linha só.
 *
 * Empilha quando a largura real do contêiner não comporta todos os botões
 * de ícone MAIS o piso do campo de texto. Enquanto a largura ainda não foi
 * medida (`containerWidthPx <= 0`, primeiro paint antes do ResizeObserver
 * disparar), o padrão é SEGURO: assume empilhado — nunca mostra o campo
 * espremido, mesmo por um frame.
 */
export function shouldUseCompactComposerLayout(
  containerWidthPx: number,
  buttonCount: number,
): boolean {
  if (containerWidthPx <= 0) return true;
  if (buttonCount <= 0) return false;

  // N botões + 1 campo = N+1 itens = N espaços de gap.
  const requiredWidthPx =
    buttonCount * COMPOSER_BUTTON_SIZE_PX +
    buttonCount * COMPOSER_GAP_PX +
    COMPOSER_ROW_PADDING_PX +
    COMPOSER_FIELD_MIN_WIDTH_PX;

  return containerWidthPx < requiredWidthPx;
}
